import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsDefaultMaterial,
  HsGraphicCollection,
  HsGraphicLines,
  HsGraphicObject,
  HsIndexedGeometryData,
  HsMatrix4,
  HsQuaternion,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateCubeGeometryData,
  hsGenerateSphereGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsGeometry,
  type HsGraphics,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsMaterial,
  type HsRegularGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeSkyDome, placeSkyDome } from '../../functions/custom-shader-material.function';
import { makeVertexColorMaterial } from './vertex-color-material.function';

const CAMERA_MATRIX: HsMatrix4 = HsMatrix4.compose(
  [0, 9, 22],
  HsQuaternion.makeFromAxisAndAngle([-0.25, 0, 0], 0.97).values,
  [1, 1, 1]
);
const DELTA_ROTATION: HsMatrix4 = HsMatrix4.compose(
  [0, 0, 0],
  HsQuaternion.makeFromAxisAndAngle([0, 1, 0], 0.01).values,
  [1, 1, 1]
);
const SPHERE_SCALE: number = 5;
/** Slightly larger than the spheres so the wireframe stays in front of their faces. */
const WIREFRAME_SCALE: number = 5.007;
/** Vertices that share one color: two triangles, one quad of the sphere. */
const COLOR_GROUP_VERTICES: number = 6;
const GROUND_SIZE: number = 40;

/** Random colors for every quad of the sphere, one RGB triple per vertex. */
function makeQuadColors(vertexCount: number): number[] {
  const colors: number[] = [];
  for (let first: number = 0; first < vertexCount; first += COLOR_GROUP_VERTICES) {
    const color: HsHypersceneTypes.Array3 = [Math.random(), Math.random(), Math.random()];
    for (let vertex: number = first; vertex < Math.min(first + COLOR_GROUP_VERTICES, vertexCount); vertex++) {
      colors.push(...color);
    }
  }
  return colors;
}

/** A checkerboard of instanced cubes. */
function makeCheckerGround(
  graphics: HsGraphics,
  light: HsHypersceneTypes.Array3,
  dark: HsHypersceneTypes.Array3,
  height: number
): HsGraphicCollection | null {
  const geometry: HsGeometry | null = hsMakeIndexedGeometry(graphics, hsIndexGeometry(hsGenerateCubeGeometryData()));
  if (geometry === null) {
    return null;
  }
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
  shaderData.addColor().addDepth().addNormals().addDiffuseLighting();
  const material: HsMaterial = new HsDefaultMaterial(graphics.gl, shaderData.makeShader());
  const ground: HsGraphicCollection = new HsGraphicCollection(getUuid(), 'ground', geometry, material);
  for (let i: number = 0; i < GROUND_SIZE; i++) {
    for (let j: number = 0; j < GROUND_SIZE; j++) {
      const matrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix().setPosition([
        i * 2 - GROUND_SIZE,
        height,
        j * 2 - GROUND_SIZE,
      ]);
      ground.addInstance(matrix.values, (i + j) % 2 === 1 ? dark : light);
    }
  }
  ground.updateCenter();
  ground.updateBoundingSphere();
  ground.updateBuffers(graphics);
  return ground;
}

/**
 * Two rotating spheres whose quads have random colors, a regular and an indexed one, each with a wireframe,
 * over a checkerboard under a sky.
 */
@Component({
  selector: 'hyperscene-composite-materials',
  templateUrl: './composite-materials.component.html',
  styleUrls: ['./composite-materials.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class CompositeMaterialsDemo extends DemoViewerPage {
  private readonly rotatingObjects: HsGraphicObject[] = [];
  private skyDome: HsGraphicObject | null = null;

  protected initScene(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const sphereVertices: ArrayLike<number> = hsGenerateSphereGeometryData(20).vertices;
    const vertexCount: number = sphereVertices.length / 3;
    const indices: number[] = Array.from({ length: vertexCount }, (_: unknown, index: number) => index);

    const sphereGeometry: HsRegularGeometry | null = hsMakeGeometry(
      graphics,
      new HsRegularGeometryData(sphereVertices, makeQuadColors(vertexCount))
    );
    const indexedSphereGeometry: HsGeometry | null = hsMakeIndexedGeometry(
      graphics,
      new HsIndexedGeometryData(sphereVertices, makeQuadColors(vertexCount), indices)
    );
    const wireframeGeometry: HsRegularGeometry | null = hsMakeGeometry(
      graphics,
      new HsRegularGeometryData(sphereVertices, [])
    );
    if (sphereGeometry === null || indexedSphereGeometry === null || wireframeGeometry === null) {
      return;
    }

    const sphereMaterial: HsMaterial = makeVertexColorMaterial(viewer.gl);
    const wireframeShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single).addColor().addDepth();
    const wireframeMaterial: HsMaterial = new HsDefaultMaterial(viewer.gl, wireframeShaderData.makeShader());
    wireframeMaterial.color.set(0, 0, 0);

    this.addSphere(viewer, sphereGeometry, sphereMaterial, wireframeGeometry, wireframeMaterial, [3, 7, -6]);
    this.addSphere(viewer, indexedSphereGeometry, sphereMaterial, wireframeGeometry, wireframeMaterial, [-3, 7, 6]);

    const ground: HsGraphicCollection | null = makeCheckerGround(graphics, [0.8, 0.8, 0.8], [0.3, 0.3, 0.3], -2);
    if (ground !== null) {
      viewer.add(ground);
    }

    this.skyDome = makeSkyDome(graphics);
    if (this.skyDome !== null) {
      viewer.add(this.skyDome);
    }

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.copy(CAMERA_MATRIX);
    viewer.camera.updateProjectionMatrix();
  }

  protected override animate(viewer: HsHypersceneViewer): boolean {
    for (const object of this.rotatingObjects) {
      object.worldMatrix.multiply(DELTA_ROTATION);
    }
    if (this.skyDome !== null) {
      placeSkyDome(this.skyDome, viewer.camera);
    }
    // Spheres turn about their own centers, so their bounding spheres and the culling result stay valid.
    return false;
  }

  private addSphere(
    viewer: HsHypersceneViewer,
    geometry: HsGeometry,
    material: HsMaterial,
    wireframeGeometry: HsRegularGeometry,
    wireframeMaterial: HsMaterial,
    position: HsHypersceneTypes.Array3
  ): void {
    const sphere: HsGraphicObject = new HsGraphicObject(getUuid(), 'sphere', geometry, material);
    sphere.worldMatrix.setPosition(position).scale([SPHERE_SCALE, SPHERE_SCALE, SPHERE_SCALE]);
    sphere.updateBoundingSphere();
    viewer.add(sphere);

    const wireframe: HsGraphicLines = new HsGraphicLines(getUuid(), 'wireframe', wireframeGeometry, wireframeMaterial);
    wireframe.worldMatrix.setPosition(position).scale([WIREFRAME_SCALE, WIREFRAME_SCALE, WIREFRAME_SCALE]);
    wireframe.updateBoundingSphere();
    viewer.add(wireframe);

    this.rotatingObjects.push(sphere, wireframe);
  }
}
